import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'motion/react';

/**
 * A silent looping product video that only plays while it is on screen (and
 * never under Reduce Motion, where the poster frame stands in). Muted +
 * playsInline is what lets iOS Safari autoplay it.
 */
export default function AutoVideo({
  src, poster, width, height, label, eager = false,
}: { src: string; poster: string; width: number; height: number; label: string; eager?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (reduce) {
      v.pause();
      return;
    }
    if (!('IntersectionObserver' in window)) {
      v.play().catch(() => {});
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [reduce]);

  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      width={width}
      height={height}
      muted
      loop
      playsInline
      preload={eager ? 'auto' : 'metadata'}
      aria-label={label}
    />
  );
}
